import { MenuItemData, isMenuItemLink } from '../menu-item-data'
import { useMenuContext } from './use-menu-context'

import { KeyboardEvent, MouseEvent, useCallback } from 'react'

type MenuButtonHookProps = {
  menuItem: MenuItemData
}

type UseMenuItemResult = {
  onMenuClick: (e: MouseEvent) => void
  onMouseEnter: (e: MouseEvent) => void
  onKeyDown: (e: KeyboardEvent) => void
  hasPopup: boolean
  isExpanded: boolean
  className: string | undefined
  role: string
}

export const useMenuItem = ({ menuItem }: MenuButtonHookProps): UseMenuItemResult => {
  const { selected, setSelected, setShowFocus, visitLink } = useMenuContext()

  const onMouseEnter = useCallback(() => {
    setShowFocus(false)
    setSelected(menuItem)
  }, [setShowFocus, setSelected, menuItem])

  const hasPopup = !isMenuItemLink(menuItem)
  const isExpanded = hasPopup && selected === menuItem
  const className = selected === menuItem ? 'selected' : undefined

  const onMenuClick = useCallback(() => {
    if (isMenuItemLink(menuItem)) {
      visitLink(menuItem)
    } else if (menuItem.submenuItems) {
      if (!selected) {
        setShowFocus(true)
        setSelected(menuItem)
      }
    }
  }, [visitLink, selected, setSelected, setShowFocus, menuItem])

  const onKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      const SPACE = ' '
      switch (event.key) {
        case SPACE:
        case 'Enter':
          onMenuClick()
          event.preventDefault()
          break
        default:
          break
      }
    },
    [onMenuClick]
  )
  // role is set to menuitem because the parent ul has role="menu"
  return { onMenuClick, onMouseEnter, onKeyDown, hasPopup, isExpanded, className, role: 'menuitem' }
}
